import {
  configure,
  defineRule,
} from 'vee-validate'
import AllRules from '@vee-validate/rules'
import {
  localize,
  setLocale as setValidationLocale
} from '@vee-validate/i18n'
import { i18n } from './i18n'
import moment from 'moment'
import ja from '@vee-validate/i18n/dist/locale/ja.json'
import en from '@vee-validate/i18n/dist/locale/en.json'
import i18next from 'i18next'
import { z } from 'zod'
import { zodI18nMap } from 'zod-i18n-map'
import translation from 'zod-i18n-map/locales/ja/zod.json'
import { siteInfoMap } from '../modules/sitesInfoMapConst'

i18next.init({
  lng: 'ja',
  resources: {
    ja: { zod: translation },
  },
})
z.setErrorMap(zodI18nMap)


Object.keys(AllRules).forEach(rule => {
  defineRule(rule, AllRules[rule])
})

defineRule('password', (value, _, ctx) => {
  if (value === '') return true
  if (RegExp(/(?=.*?[A-Za-z])(?=.*?[0-9]).{8,128}$/).test(value)) {
    return true
  } else {
    return i18n.t('validations.password', { value: ctx.field })
  }
})

defineRule('email', (value) => {
  // https://github.com/logaretm/vee-validate/blob/main/packages/rules/src/email.ts
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  if (re.test(value)) {
    return true
  } else {
    return i18n.t('validations.email')
  }
})

defineRule('postal_code', (value) => {
  if (value == null) return true
  if (!value.includes('-')) {
    return true
  } else {
    return i18n.t('validations.postalCode')
  }
})

defineRule('tel', (value) => {
  if (value == null) return true
  if (!value.includes('-')) {
    return true
  } else {
    return i18n.t('validations.tel')
  }
})

defineRule('excludedWord', (value, target) => {
  if (value == null) return true
  if (!target.some(text => value.includes(text))) {
    return true
  }
  return i18n.t('validations.excludedWord', { value: target.join(',') })
})

defineRule('confirmed', (value, [target]) => {
  if (value === target) {
    return true
  }
  return 'パスワードと一致しません'
})

defineRule(
  'postImage',
  async (postImages, [minWidth = 400, minHeight = 300] = []) => {
    if (!postImages) {
      return true
    }
    let targetImages = postImages
    if (!Array.isArray(targetImages)) {
      targetImages = [targetImages]
    }
    const _postImages = []
    targetImages.forEach((postImage) => {
      _postImages.push(postImage)
    })

    const dimensions = []

    await Promise.all(
      _postImages.map((postImage) => {
        return new Promise((resolve) => {
          const image = new Image()
          const reader = new FileReader()
          reader.readAsDataURL(postImage)

          reader.onload = () => {
            image.src = reader.result

            image.onload = () => {
              dimensions.push({
                width: image.naturalWidth,
                height: image.naturalHeight,
              })
              resolve()
            }
          }
        })
      })
    )

    const results = dimensions.map((postImage) => {
      return postImage.width >= minWidth && postImage.height >= minHeight
    })
    if (results.every((result) => result)) {
      return true
    } else {
      return `画像の大きさは縦横${minHeight}×${minWidth}ピクセル以上にしてください`
    }
  }
)

defineRule('gmbCoverImage', async (value) => {
  let width = 0
  let height = 0
  await new Promise((resolve) => {
    const image = new Image()
    image.src = value
    image.onload = () => {
      height = image.naturalHeight
      width = image.naturalWidth
      resolve()
    }
    image.onerror = () => {
      resolve()
    }
  })
  //画像読み込めなかった時はbackendのバリデーションに任せる
  if (width === 0 || height === 0) return true

  const aspectRatio = width / height
  // GMB / Yahoo Place APIのカバー写真のサイズバリデーションに合わせて, アスペクトは16:9を中間に幅がある形
  if ((1280 <= width && 720 <= height) && (width <= 2120 && height <= 1192) && (1.482 <= aspectRatio && aspectRatio <= 2.222)) {
    return true
  } else {
    return i18n.t('validations.gmbCoverImage')
  }
})

defineRule('eventDate', (dates) => {
  // 現在の日付よりも指定された日付のほうが過去の場合はエラー
  const newDate = new Date()
  let errors = []

  Object.keys(dates).forEach(key => {
    const currentDate = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate())
    const eventDate = new Date(dates[key].getFullYear(), dates[key].getMonth(), dates[key].getDate())
    errors.push(eventDate.getTime() >= currentDate.getTime())
  })

  if (errors[0] === false && errors[1] === true) {
    return true // スタートの日付が過去でも、終了の日付が未来であればエラーにならない
  }
  if (errors.every(error =>  error === true )) {
    return true
  } else {
    return '過去の日付を指定することはできません'
  }
})

defineRule('after_pre_time', (startTime, [endTime]) => {
  if (!endTime) return true // 終了時刻が入力されていない場合は true を返す
  if (moment(startTime, 'HH:m').isAfter(moment(endTime, 'HH:m')) || startTime === endTime) {
    return true
  } else {
    return i18n.t('validations.afterPreTime')
  }
})

defineRule('time_format', (value) => {
  if (!value) return true
  if (moment(value, 'HH:m', true).isValid()) {
    return true
  } else {
    return i18n.t('validations.timeFormat')
  }
})

// TODO: これ使われているか確認
defineRule('time', (time) => {
  if (time.match(/^([01]?[0-9]|2[0-3]):([0-5][0-9])$/) !== null) {
    return true
  } else {
    return '有効な時間の形式を入力してください\n（例: 08:00 AM）'
  }
})
defineRule('before_time', (value, [target]) => {
  if (!target) return true
  if (moment(value, 'H:m').isBefore(moment(target, 'H:m'))) {
    return true
  } else {
    return i18n.t('validations.beforeTime')
  }
})
defineRule('after_time', (value, [target]) => {
  if (moment(value, 'H:m').isAfter(moment(target, 'H:m'))) {
    return true
  } else {
    return i18n.t('validations.afterTime')
  }
})

defineRule('url', (value) => {
  if (value === '') return true
  if (RegExp(/(http|https):\/\/(.+)/).test(value)) {
    return true
  } else {
    return i18n.t('validations.url')
  }
})

defineRule('asoview_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['asoview']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('google_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['google']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('tabelog_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['tabelog']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('gnavi_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['gnavi']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('hot_pepper_gourmet_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['hot_pepper_gourmet']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('hot_pepper_beauty_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['hot_pepper_beauty']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('retty_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['retty']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('trip_advisor_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['trip_advisor']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('yahoo_loco_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['yahoo_loco']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('demaecan_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['demaecan']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('ikyu_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['ikyu']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('ozmall_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['ozmall']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('facebook_url', (value) => {
  if (value == null || value === '') return true
  if (RegExp(siteInfoMap['facebook']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('twitter_url', (value) => {
  if (value == null || value === '') return true
  if (RegExp(siteInfoMap['twitter']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('instagram_url', (value) => {
  if (value == null || value === '') return true
  if (RegExp(siteInfoMap['instagram']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('line_url', (value) => {
  if (value == null || value === '') return true
  if (RegExp(siteInfoMap['line']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('report_clinic_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['report_clinic']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('tribeau_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['tribeau']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

defineRule('taishu_tenpyo_url', (value) => {
  if (value === '') return true
  if (RegExp(siteInfoMap['taishu_tenpyo']['regex']).test(value)) {
    return true
  } else {
    return i18n.t('validations.siteUrlFormat')
  }
})

export const boundaryOfJapanMap = {
  northwest: {
    latitude: 45.3326,
    longitude: 122.5557
  },
  southeast: {
    latitude: 20.2531,
    longitude: 153.5912
  }
}

export const isValidLatitude = (value) => {
  return boundaryOfJapanMap.southeast.latitude < value && boundaryOfJapanMap.northwest.latitude > value
}

export const isValidLongitude = (value) => {
  return boundaryOfJapanMap.northwest.longitude < value && boundaryOfJapanMap.southeast.longitude > value
}

defineRule('latitude', value => isValidLatitude(value) || i18n.t('messages.invalid'))
defineRule('longitude', value => isValidLongitude(value) || i18n.t('messages.invalid'))

defineRule('maxHashtag', (value, [max]) => {
  var count = (value.match(/#/g) || []).length
  if (count > Number(max)) {
    return `1つの投稿に含められるハッシュタグは${max}個までです`
  }
  return true
})
defineRule('maxAtSign', (value, [max]) => {
  var count = (value.match(/@/g) || []).length
  if (count > Number(max)) {
    return `1つの投稿に含められる@は${max}個までです`
  }
  return true
})

const week = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
const judgeOverNight = (startTime, endTime) => moment(startTime, 'HH:m').isAfter(moment(endTime, 'HH:m'))
defineRule('clash_with_other_regular_hour', (_, [startTime, endTime, wday, regularHours]) => {
  if (!startTime || !endTime || !wday) return true // 開始時刻、終了時刻のどちらかが入力されていない場合、祝日・祝前日の場合は true を返す
  if (startTime < endTime) return true
  const nextWDay = wday === 'saturday' ? 'sunday' : week[week.indexOf(wday) + 1]
  const nextPeriods = regularHours.find(hour => hour.wday === nextWDay).periods
  const nextStartTime = nextPeriods[nextPeriods.length - 1]?.openTime
  if (!nextStartTime) return true

  if(nextStartTime < endTime) return i18n.t('messages.crushExistingBusinessHour')
  return true
})

defineRule('check_min', (_, [min, checkArray]) => {
  if (checkArray.length < Number(min)) return i18n.t('messages.errorMinCheck', { min: min})
  return true
})
defineRule('valid_last_order', (value, [openTime, closeTime]) => {
  if (!value || !openTime || !closeTime) return true
  const isOverNightPeriod = judgeOverNight(openTime, closeTime)

  if (isOverNightPeriod) {
    if (moment(value, 'H:m').isAfter(moment(openTime, 'H:m')) || moment(value, 'H:m').isBefore(moment(closeTime, 'H:m'))) {
      return true
    } else {
      return i18n.t('validations.validLastOrder')
    }
  } else {
    if (moment(value, 'H:m').isAfter(moment(openTime, 'H:m')) && moment(value, 'H:m').isBefore(moment(closeTime, 'H:m'))) {
      return true
    } else {
      return i18n.t('validations.validLastOrder')
    }
  }
})

defineRule('action_type_required', value => {
  return value !== 'ACTION_TYPE_UNSPECIFIED' || i18n.t('messages.notSelected')
})

configure({
  generateMessage: localize({
    ja,
    en,
  }),
})

export { setValidationLocale }
